"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useSession } from "next-auth/react";
import type { Establishment } from "@/types";

interface AuthContextValue {
  token: string | null;
  establishment: Establishment | null;
  baseURL: string | null;
  currency: string;
  isLoading: boolean;
  setToken: (token: string | null) => void;
  setEstablishment: (establishment: Establishment | null) => void;
  setBaseURL: (url: string | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { data: session, status } = useSession();
  const [token, setToken] = useState<string | null>(null);
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null,
  );
  const [baseURL, setBaseURL] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      const customSession = session as any;
      const accessToken = customSession?.tokens?.access;
      const userEstablishment = customSession?.user?.user?.establishment;

      setToken(accessToken || null);
      setEstablishment(userEstablishment || null);

      const establishmentSlug = userEstablishment?.slug;
      const newBaseURL = establishmentSlug
        ? `${process.env.NEXT_PUBLIC_API_URL}/establishment/${establishmentSlug}`
        : process.env.NEXT_PUBLIC_API_URL;

      setBaseURL(newBaseURL || null);
    } else {
      setToken(null);
      setEstablishment(null);
      setBaseURL(process.env.NEXT_PUBLIC_API_URL || null);
    }
  }, [session]);

  const currency = establishment?.currency || "BRL";
  const isLoading = status === "loading";

  const value: AuthContextValue = {
    token,
    establishment,
    baseURL,
    currency,
    isLoading,
    setToken,
    setEstablishment,
    setBaseURL,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
